import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {ColumnDef} from "@tanstack/react-table";
import Property from "crqlar-react-common/dist/components/Property";
import type {ImportLog} from "crqlar-react-common/dist/models/integrations/importLog.model";
import type {OTAStats} from "crqlar-react-common/dist/models/integrations/otaStats.model";
import {ImportLogService} from "crqlar-react-common/dist/services/integrations/importLog.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {formatDateDMYHM, formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "react-query";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    ServerTableToolbarAction,
    useServerTable
} from "../../../common/serverTable";
import ImportLogPreviewDialog from "../../common/ImportLogPreviewDialog";

interface Props {
    configId?: string
    connectorId: string
    getStats: (configId: string) => Promise<OTAStats>
}

const StatsImportLogsTable = ({configId, connectorId, getStats}: Props) => {
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const [selectedLog, setSelectedLog] = useState<ImportLog>();

    const statsQuery = useQuery(
        ['integrationImportStats', connectorId, configId],
        () => getStats(configId!),
        {enabled: !!configId, refetchInterval: 5000}
    );

    const columns = useMemo<ColumnDef<ImportLog>[]>(() => [
        {
            id: 'contentCategory',
            header: t('Content category'),
            accessorFn: log => log.contentCategory,
            cell: ({row}) => <Box>{row.original.contentCategory || '-'}</Box>,
            size: 220,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: log => log.status,
            cell: ({row}) => <Box>{row.original.status || '-'}</Box>,
            size: 140,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'processingStart',
            header: t('Processing start'),
            accessorFn: log => log.processingStart,
            cell: ({row}) => <Box>{formatDateDMYHM(row.original.processingStart) || '-'}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'processingDone',
            header: t('Processing done'),
            accessorFn: log => log.processingDone,
            cell: ({row}) => <Box>{formatDateDMYHM(row.original.processingDone) || '-'}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'message',
            header: t('Message'),
            accessorFn: log => log.message,
            cell: ({row}) => <Box>{row.original.message?.substring(0, 300) || '-'}</Box>,
            size: 300,
            meta: {defaultActive: true, grow: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: log => log.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 300,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: log => log.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: `system-${connectorId}-stats-import-logs`,
        columns,
        getRowId: log => log.id!,
        enabled: !!configId,
        queryDependencies: [configId],
        initialSorting: [{id: 'processingStart', desc: true}],
        onRowClick: setSelectedLog,
        queryFn: async ({pagination, sorting}) => {
            const page = await ImportLogService.getAll({
                configIds: [configId!],
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    const reprocessMutation = useMutation(
        ['reprocessIntegrationImports', connectorId, configId],
        () => ImportLogService.reprocess(configId!),
        {
            onSuccess: async () => {
                await Promise.all([
                    statsQuery.refetch(),
                    controller.query.refetch()
                ]);
            }
        }
    );

    const stats = statsQuery.data;

    return (
        <Stack gap={2}>
            <Stack width="100%" gap={1}>
                <Typography variant="h6" textAlign="start">
                    {t('Stats')}
                </Typography>

                {statsQuery.isError && (
                    <Alert severity="error">
                        {t('Something went wrong')}
                    </Alert>
                )}

                <Property title={t('Pending requests')}>
                    <Typography>{stats?.pendingImports || 0}</Typography>
                </Property>
                <Property title={t('In progress requests')}>
                    <Typography>{stats?.inProgressImports || 0}</Typography>
                </Property>
                <Property title={t('Successful requests')}>
                    <Typography>{stats?.successfulImports || 0}</Typography>
                </Property>
                <Property title={t('Failed requests')}>
                    <Typography>{stats?.failedImports || 0}</Typography>
                </Property>
                <Property title={t('Oldest pending import')}>
                    <Typography>{formatDateDMYHM(stats?.oldestPendingImport) || '-'}</Typography>
                </Property>
                <Property title={t('Last successful import')}>
                    <Typography>{formatDateDMYHM(stats?.mostRecentProcessedImport) || '-'}</Typography>
                </Property>
            </Stack>

            <Box sx={{height: '70vh', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <ServerTableToolbar>
                            <ServerTableSort/>
                            <ServerTableFullscreen/>

                            <ServerTableColumns/>

                            <ServerTableToolbarAction
                                variant="contained"
                                loading={reprocessMutation.isLoading}
                                disabled={!configId || readOnly}
                                onClick={() => reprocessMutation.mutate()}
                            >
                                {t('Mark all imports for (re)processing')}
                            </ServerTableToolbarAction>
                        </ServerTableToolbar>

                        <ServerTable/>
                    </ServerTableFrame>
                </ServerTableProvider>
            </Box>

            <ImportLogPreviewDialog
                open={!!selectedLog}
                importLogId={selectedLog?.id}
                onClose={() => setSelectedLog(undefined)}
            />

            <Snackbar
                open={reprocessMutation.isError}
                autoHideDuration={6000}
                onClose={() => reprocessMutation.reset()}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Alert severity="error" onClose={() => reprocessMutation.reset()}>
                    {t('Something went wrong')}
                </Alert>
            </Snackbar>
        </Stack>
    );
};

export default StatsImportLogsTable;
